import { ThreeEvent, useFrame } from '@react-three/fiber'
import { severityStyles } from 'pages/pointClouds/components/dropdowns/EnumStyles'
import React, { useMemo, useRef } from 'react'
import { Mesh } from 'three'
import * as THREE from 'three'
import { GeckoSiteObservation } from 'types'

export interface MarkerBallProps {
  observation: GeckoSiteObservation
  onActivate: (ev: MouseEvent) => void
  onDeactivate: () => void
}

export const MarkerBall: React.FC<MarkerBallProps> = ({
  observation,
  onActivate,
  onDeactivate,
}) => {
  const meshRef = useRef<Mesh>(null)
  const mainColor = useMemo(() => {
    return new THREE.Color(0xad7f35)
  }, [])

  const markerColor = useMemo(() => {
    if (
      observation.data?.severity &&
      severityStyles[observation.data.severity]
    ) {
      return new THREE.Color(severityStyles[observation.data.severity].color)
    }
    return mainColor
  }, [observation.data?.severity, mainColor])

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.x += 0.01
      meshRef.current.rotation.y += 0.01
    }
  })

  const handlePointerEnter = (ev: ThreeEvent<PointerEvent>) => {
    const mouseEvent = ev.nativeEvent
    if (!meshRef.current) return // Prevents error when meshRef is null
    onActivate(mouseEvent)
  }

  if (!observation.data?.pointCloudCoordinates?.coordinate) {
    return null
  }

  const position = observation.data?.pointCloudCoordinates?.coordinate

  return (
    <group>
      <mesh
        ref={meshRef}
        position={[position[0], position[1], position[2]]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={onDeactivate}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial
          color={markerColor}
          roughness={0}
          metalness={0}
          emissive={markerColor}
          emissiveIntensity={0.3}
        />
      </mesh>
      <mesh // This is the mesh that is used to detect hover
        position={[position[0], position[1], position[2]]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={onDeactivate}
        layers={1} // Stops EDL from affecting this ball
      >
        <sphereGeometry args={[0.9, 32, 32]} />
        <meshStandardMaterial transparent opacity={0} />
      </mesh>
    </group>
  )
}
